import { useQuery } from "@tanstack/react-query";
import { fetcher } from "../../utils/fetchers";
import { getAdminAccessToken, getStoreAccessToken } from "../../utils/auth";
import { productTopOrderType } from "../../types/modules/product.type";

interface UseParams {
  filter?: string;
  date?: string;
  storeId?: number;
  fromDate?: string;
  toDate?: string;
}

export const useStoreFinancials = ({
  filter = "",
  date = "",
  storeId,
  fromDate,
  toDate,
}: UseParams) => {
  const queryParams = new URLSearchParams({
    filter: filter.toString(),
  });

  if (date) {
    queryParams.append("specificDate", date);
  }

  if (fromDate) {
    queryParams.append("from", fromDate);
  }

  if (toDate) {
    queryParams.append("to", toDate);
  }

  if (storeId) {
    queryParams.append("storeId", storeId.toString());
  }

  const token = storeId ? getAdminAccessToken() : getStoreAccessToken();
  return useQuery({
    queryKey: [
      "store-homeui-financials",
      filter,
      date,
      storeId,
      fromDate,
      toDate,
    ],
    queryFn: () =>
      fetcher<{
        availableBalance: number;
        totalCanceled: number;
        totalRefunded: number;
        totalEarning: number;
      }>(
        `store-transaction/financials/byStore?${queryParams.toString()}`,
        token
      ),
  });
};

export const useStoreOrdersStatistics = ({
  filter = "",
  date = "",
  storeId,
  fromDate,
  toDate,
}: UseParams) => {
  const queryParams = new URLSearchParams({
    filter: filter.toString(),
  });

  if (date) {
    queryParams.append("specificDate", date);
  }

  if (fromDate) {
    queryParams.append("from", fromDate);
  }

  if (toDate) {
    queryParams.append("to", toDate);
  }

  if (storeId) {
    queryParams.append("storeId", storeId.toString());
  }

  const token = storeId ? getAdminAccessToken() : getStoreAccessToken();

  return useQuery({
    queryKey: [
      "store-homeui-order-statistics",
      filter,
      date,
      storeId,
      fromDate,
      toDate,
    ],
    queryFn: () =>
      fetcher<{
        cancelledCount: number;
        completedCount: number;
        scheduledCount: number;
      }>(`order/stats/byStore?${queryParams.toString()}`, token),
  });
};

export const useTopFourProductOrdered = ({
  filter = "",
  date = "",
  storeId,
  fromDate,
  toDate,
}: UseParams) => {
  const queryParams = new URLSearchParams({
    filter: filter.toString(),
  });

  if (date) {
    queryParams.append("specificDate", date);
  }

  if (fromDate) {
    queryParams.append("from", fromDate);
  }

  if (toDate) {
    queryParams.append("to", toDate);
  }

  if (storeId) {
    queryParams.append("storeId", storeId.toString());
  }

  const token = storeId ? getAdminAccessToken() : getStoreAccessToken();

  return useQuery({
    queryKey: [
      "store-homeui-product-top-sales-four",
      filter,
      date,
      storeId,
      fromDate,
      toDate,
    ],
    queryFn: () =>
      fetcher<productTopOrderType[]>(
        `product/top-sales/byStore?${queryParams.toString()}`,
        token
      ),
  });
};

export const useStoreOrdersAnalytics = ({
  filter = "",
  date = "",
  storeId,
  fromDate,
  toDate,
}: UseParams) => {
  const queryParams = new URLSearchParams({
    filter: filter.toString(),
  });

  if (date) {
    queryParams.append("specificDate", date);
  }

  if (fromDate) {
    queryParams.append("from", fromDate);
  }

  if (toDate) {
    queryParams.append("to", toDate);
  }

  if (storeId) {
    queryParams.append("storeId", storeId.toString());
  }

  const token = storeId ? getAdminAccessToken() : getStoreAccessToken();

  return useQuery({
    queryKey: [
      "store-homeui-order-analytics",
      filter,
      date,
      storeId,
      fromDate,
      toDate,
    ],
    queryFn: () =>
      fetcher<{
        averagePrepTime: number;
        customerRepeatRate: number;
      }>(`order/analytics/byStore?${queryParams.toString()}`, token),
  });
};
